import React from 'react';
import { Box, CircularProgress, Divider, IconButton, Tooltip, useTheme } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { motion, useReducedMotion } from 'framer-motion';
import { useIntl } from 'react-intl';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { IconTemplate } from '@tabler/icons-react';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import { useSelector } from 'store';
import useFunnerInternalAccess from 'hooks/useFunnerInternalAccess';
import { isAccountModuleEnabled, type AccountModuleId } from 'types/account/accountModules';
import { openRightSidebar } from 'store/slices/dual-sidebar';
import { getChatSidebarPanelWidthSync } from 'utils/chat-list-panel-width';
import type { ChatToolsRailPanelId } from './chat-tools-rail-ids';
import { CHAT_TOOLS_DOCKED_CLOSE_BTN_DIAMETER_PX, CHAT_TOOLS_RAIL_WIDTH_PX } from './chat-tools-rail-layout';
import { CHAT_FLOATING_RAIL_Z_INDEX } from './layout-zoom-constants';
import { exportChatConversationToExcel } from 'utils/export-chat-to-excel';
import { enqueueErrorHebrew, enqueueSuccessHebrew } from 'utils/appSnackbar';
import useLocalization from 'hooks/useLocalization';

export {
  CHAT_TOOLS_RAIL_FLOAT_MARGIN_PX,
  CHAT_TOOLS_RAIL_WIDTH_PX,
  CHAT_TOOLS_RAIL_INLINE_RESERVE_PX,
} from './chat-tools-rail-layout';

const RAIL_ENTRANCE = { duration: 0.38, ease: [0.22, 1, 0.36, 1] as const };

/** ה־Tooltip הגלובלי מסמן את הטריגר ב־`data-mui-tooltip-anchor` ואז הערכת מכריחה `.MuiSvgIcon-root` ל־14px — רק התבניות (Tabler) נשארות בגודל מלא; כאן דורסים במפורש. */
const RAIL_ICON_BUTTON_SVG_ICON_SX = {
  '&& .MuiSvgIcon-root': {
    fontSize: 22,
    width: 22,
    height: 22,
  },
} as const;

function railNavBorderRadius(theme: Theme): string {
  const r = theme.shape.borderRadius;
  const n = typeof r === 'number' ? r : 8;
  return `${Math.min(24, Math.max(n * 2, n))}px`;
}

export type ChatToolsVerticalRailVariant = 'floating' | 'docked';

export type ChatToolsVerticalRailProps = {
  variant?: ChatToolsVerticalRailVariant;
  /** במצב docked — סגירת הפאנל הצדדי; הכפתור מוצג בראש עמודת האייקונים */
  onDockedClose?: () => void;
};

type RailPanelItem = {
  kind: 'panel';
  id: ChatToolsRailPanelId;
  tooltipId: string;
  titleMessageId: string;
  icon: React.ReactNode;
  requiresChatUser?: boolean;
  /** פנימי Funner בלבד — כמו ops-console */
  funnerInternalOnly?: boolean;
  /** מוצג רק כשמודול החשבון הזה דלוק לחברה (ops console) */
  accountModule?: AccountModuleId;
  /** הפאנל מספק כותרת משלו (AIAssistantPane) — לא להציג כותרת מגירה כפולה */
  hideHeader?: boolean;
};

type RailActionItem = {
  kind: 'action';
  id: 'export-chat-excel';
  tooltipId: string;
  icon: React.ReactNode;
  requiresChatUser?: boolean;
};

type RailItem = RailPanelItem | RailActionItem;

/** סדר מלמעלה למטה: תבניות → סוכן AI → בוטים → שיחות → חיפוש → הסרות → ייצוא Excel */
const RAIL_ITEMS: RailItem[] = [
  {
    kind: 'panel',
    id: 'chat-templates-menu',
    tooltipId: 'chat.toolsRail.templates',
    titleMessageId: 'chat.toolsRail.templates',
    icon: <IconTemplate size={22} stroke={1.5} aria-hidden />,
  },
  {
    kind: 'panel',
    id: 'ask-ai-dialog',
    tooltipId: 'chat.toolsRail.ai',
    titleMessageId: 'chat.header.chatWindow.askAI.sidebarTitle',
    icon: <AutoAwesomeOutlinedIcon sx={{ fontSize: 22 }} aria-hidden />,
    requiresChatUser: true,
    hideHeader: true,
  },
  {
    kind: 'panel',
    id: 'chat-bot-status-menu',
    tooltipId: 'chat.toolsRail.bot',
    titleMessageId: 'chat.header.chatWindow.botStatus.sidebarTitle',
    icon: <SmartToyOutlinedIcon sx={{ fontSize: 22 }} aria-hidden />,
    requiresChatUser: true,
  },
  {
    kind: 'panel',
    id: 'chat-user-conversations-menu',
    tooltipId: 'chat.toolsRail.conversations',
    titleMessageId: 'chat.header.chatWindow.conversations.sidebarTitle',
    icon: <ForumOutlinedIcon sx={{ fontSize: 22 }} aria-hidden />,
    requiresChatUser: true,
  },
  {
    kind: 'panel',
    id: 'contact-service-calls',
    tooltipId: 'chat.toolsRail.serviceCalls',
    titleMessageId: 'chat.serviceCallsPanel.title',
    icon: <SupportAgentOutlinedIcon sx={{ fontSize: 22 }} aria-hidden />,
    requiresChatUser: true,
    accountModule: 'serviceCalls',
  },
  {
    kind: 'panel',
    id: 'chat-user-search-messages',
    tooltipId: 'chat.toolsRail.search',
    titleMessageId: 'chat.search-messages.title',
    icon: <SearchOutlinedIcon sx={{ fontSize: 22 }} aria-hidden />,
    requiresChatUser: true,
  },
  {
    kind: 'panel',
    id: 'contact-opt-out-log',
    tooltipId: 'chat.toolsRail.optOutLog',
    titleMessageId: 'chat.header.chatWindow.optOutLog.sidebarTitle',
    icon: <HistoryOutlinedIcon sx={{ fontSize: 22 }} aria-hidden />,
    requiresChatUser: true,
  },
  {
    kind: 'action',
    id: 'export-chat-excel',
    tooltipId: 'chat.toolsRail.exportExcel',
    icon: <FileDownloadOutlinedIcon sx={{ fontSize: 22 }} aria-hidden />,
    requiresChatUser: true,
  },
];

function RailNavButtons({
  pillBg,
  pillBorder,
  navSxExtra,
}: {
  pillBg: (t: Theme) => string;
  pillBorder: (t: Theme) => string;
  navSxExtra?: Record<string, unknown>;
}) {
  const intl = useIntl();
  const { currentLocale } = useLocalization();
  const user = useSelector((state) => state.user);
  const modulePermissions = useSelector((state) => state.userConfig.modulePermissions);
  const { isAllowed: isFunnerInternal } = useFunnerInternalAccess();
  const canExportData = React.useMemo(() => {
    if (user?.Role === 'admin') return true;
    if (!modulePermissions) return true;
    return modulePermissions.dataExport !== false;
  }, [modulePermissions, user?.Role]);
  const accountModules = useSelector((state) => state.accountModules);
  const railItems = React.useMemo(
    () =>
      RAIL_ITEMS.filter((item) => {
        if (item.id === 'export-chat-excel' && !canExportData) return false;
        if (item.kind === 'panel' && item.funnerInternalOnly && !isFunnerInternal) return false;
        if (item.kind === 'panel' && item.accountModule && !isAccountModuleEnabled(accountModules.data, item.accountModule)) {
          return false;
        }
        return true;
      }),
    [canExportData, isFunnerInternal, accountModules],
  );
  const exportLoc = React.useMemo(
    () => (currentLocale?.chat?.exportExcel ?? {}) as Record<string, string | undefined>,
    [currentLocale],
  );
  const reduceMotion = useReducedMotion();
  const activeId = useSelector((state) => state.dualSidebar.right.activeId);
  const selectedChatUser = useSelector((state) => state.chat.selectedChatUser);
  const [exportingExcel, setExportingExcel] = React.useState(false);

  const handleSelectPanel = React.useCallback(
    (item: RailPanelItem) => {
      if (item.requiresChatUser && !selectedChatUser) return;
      openRightSidebar(item.id, {
        title: intl.formatMessage({ id: item.titleMessageId }),
        width: getChatSidebarPanelWidthSync(),
        chatPanelStyle: true,
        hideHeader: item.hideHeader,
      });
    },
    [intl, selectedChatUser],
  );

  const handleExportExcel = React.useCallback(async () => {
    if (!selectedChatUser || exportingExcel) return;
    setExportingExcel(true);
    try {
      const result = await exportChatConversationToExcel({ chatUser: selectedChatUser });
      enqueueSuccessHebrew(
        exportLoc.success ?? `הקובץ ${result.fileName} הורד בהצלחה`,
      );
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : exportLoc.error ?? 'ייצוא השיחה נכשל';
      enqueueErrorHebrew(message);
    } finally {
      setExportingExcel(false);
    }
  }, [exportLoc.error, exportLoc.success, exportingExcel, selectedChatUser]);

  const handleItemClick = React.useCallback(
    (item: RailItem) => {
      if (item.kind === 'action') {
        void handleExportExcel();
        return;
      }
      handleSelectPanel(item);
    },
    [handleExportExcel, handleSelectPanel],
  );

  return (
    <motion.div
      initial={reduceMotion ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: RAIL_ENTRANCE.duration, ease: RAIL_ENTRANCE.ease }}
      style={{
        width: '100%',
        pointerEvents: 'auto',
        maxHeight: '100%',
      }}
    >
      <Box
        component="nav"
        aria-label={intl.formatMessage({ id: 'chat.toolsRail.navLabel' })}
        sx={{
          width: '100%',
          maxHeight: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          boxSizing: 'border-box',
          py: 1,
          px: 0.25,
          gap: 0.35,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderRadius: (t) => railNavBorderRadius(t),
          bgcolor: (t) => pillBg(t),
          border: '1px solid',
          borderColor: (t) => pillBorder(t),
          scrollbarWidth: 'thin',
          '@media (prefers-reduced-motion: reduce)': {
            scrollBehavior: 'auto',
          },
          ...navSxExtra,
        }}
      >
        {railItems.map((item, index) => {
          const disabled =
            Boolean(item.requiresChatUser && !selectedChatUser) ||
            (item.kind === 'action' && exportingExcel);
          const selected = item.kind === 'panel' && activeId === item.id;
          const showDividerAfter = index === 1;
          const tooltip =
            item.kind === 'action' && exportingExcel
              ? exportLoc.loading ?? 'מייצא לאקסל…'
              : intl.formatMessage({ id: item.tooltipId });

          return (
            <React.Fragment key={item.id}>
              <Tooltip title={tooltip} placement="left" enterDelay={300}>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    py: 0.15,
                  }}
                >
                  <IconButton
                    type="button"
                    size="small"
                    disabled={disabled}
                    onClick={() => handleItemClick(item)}
                    aria-label={tooltip}
                    aria-pressed={item.kind === 'panel' ? selected : undefined}
                    aria-busy={item.kind === 'action' ? exportingExcel : undefined}
                    sx={{
                      width: 40,
                      height: 40,
                      p: 0,
                      borderRadius: '50%',
                      transition: (t) =>
                        t.transitions.create(['background-color', 'color', 'box-shadow'], {
                          duration: 200,
                          easing: t.transitions.easing.easeInOut,
                        }),
                      ...(selected
                        ? {
                            bgcolor: (t) => alpha(t.palette.secondary.main, 0.12),
                            boxShadow: (t) => t.customShadows.secondary,
                            color: 'secondary.dark',
                          }
                        : {
                            color: 'text.secondary',
                            '&:hover': {
                              bgcolor: 'action.hover',
                              color: 'secondary.dark',
                            },
                          }),
                      '@media (prefers-reduced-motion: reduce)': {
                        transition: 'none',
                      },
                      ...RAIL_ICON_BUTTON_SVG_ICON_SX,
                    }}
                  >
                    {item.kind === 'action' && exportingExcel ? (
                      <CircularProgress size={18} thickness={4} color="inherit" aria-hidden />
                    ) : (
                      item.icon
                    )}
                  </IconButton>
                </Box>
              </Tooltip>
              {showDividerAfter ? (
                <Divider
                  flexItem
                  sx={{
                    my: 0.35,
                    width: '72%',
                    alignSelf: 'center',
                    borderColor: (t) => alpha(t.palette.divider, 0.45),
                  }}
                />
              ) : null}
            </React.Fragment>
          );
        })}
      </Box>
    </motion.div>
  );
}

/**
 * סרגל כלי — מצב צף (מעל הצ'אט) או מצובת (0px לפאנל כשהמגירה פתוחה).
 */
function dockedCloseIconButtonSx(theme: Theme) {
  return {
    width: CHAT_TOOLS_DOCKED_CLOSE_BTN_DIAMETER_PX,
    height: CHAT_TOOLS_DOCKED_CLOSE_BTN_DIAMETER_PX,
    p: 0,
    borderRadius: '50%',
    bgcolor: 'background.paper',
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: theme.shadows[2],
    color: 'text.secondary',
    flexShrink: 0,
    '&:hover': {
      bgcolor: 'action.hover',
      borderColor: 'text.disabled',
    },
  } as const;
}

const ChatToolsVerticalRail: React.FC<ChatToolsVerticalRailProps> = ({ variant = 'floating', onDockedClose }) => {
  const theme = useTheme();
  const intl = useIntl();

  const pillBg = (t: typeof theme) =>
    t.palette.mode === 'dark'
      ? alpha(t.palette.background.paper, 0.55)
      : 'rgba(255, 255, 255, 0.82)';
  const pillBorder = (t: typeof theme) =>
    alpha(t.palette.mode === 'dark' ? t.palette.common.white : t.palette.divider, t.palette.mode === 'dark' ? 0.12 : 0.18);

  if (variant === 'docked') {
    return (
      <Box
        sx={{
          width: CHAT_TOOLS_RAIL_WIDTH_PX,
          flexShrink: 0,
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          boxSizing: 'border-box',
          py: 1,
          gap: 0.5,
        }}
      >
        {onDockedClose ? (
          <Tooltip
            title={intl.formatMessage({ id: 'chat.toolsRail.closeSidebar' })}
            placement="left"
            enterDelay={300}
          >
            <IconButton
              type="button"
              onClick={onDockedClose}
              aria-label={intl.formatMessage({ id: 'chat.toolsRail.closeSidebar' })}
              size="small"
              sx={(t) => dockedCloseIconButtonSx(t)}
            >
              <ChevronRightIcon sx={{ fontSize: 20 }} aria-hidden />
            </IconButton>
          </Tooltip>
        ) : null}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            justifyContent: 'center',
          }}
        >
          <RailNavButtons
            pillBg={() => 'transparent'}
            pillBorder={() => 'transparent'}
            navSxExtra={{
              borderRadius: 0,
              border: 'none',
              bgcolor: 'transparent',
              boxShadow: 'none',
              backdropFilter: 'none',
              WebkitBackdropFilter: 'none',
              flex: 1,
              minHeight: 0,
              justifyContent: 'center',
            }}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        alignSelf: 'stretch',
        width: CHAT_TOOLS_RAIL_WIDTH_PX,
        minHeight: 0,
        zIndex: CHAT_FLOATING_RAIL_Z_INDEX,
        pointerEvents: 'none',
        boxSizing: 'border-box',
      }}
    >
      <RailNavButtons
        pillBg={pillBg}
        pillBorder={pillBorder}
        navSxExtra={{
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          boxShadow: theme.customShadows.z12,
        }}
      />
    </Box>
  );
};

export default ChatToolsVerticalRail;
